import React, { useEffect, useRef, useState } from 'react';
import { Autocomplete, CircularProgress, Stack, TextField, Typography } from '@mui/material';
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined';
import _ from 'lodash';
import customerServices from 'app/services/customer-services';

const LIMIT = 20;

// El cliente (Customer) cuyos pedidos va a ver un usuario con perfil Cliente. Los clientes
// pueden ser miles, asi que se buscan en el backend a medida que se escribe.
//
// `value` es { id, name } (o null). Se busca en la compania activa: el backend valida que el
// cliente sea de la compania donde se asigna el perfil.
const ClientCustomerSelect = ({ value, onChange, error, helperText, disabled }) => {
    const [input, setInput] = useState('');
    const [options, setOptions] = useState([]);
    const [loading, setLoading] = useState(false);
    const requestId = useRef(0);

    const search = useRef(_.debounce((q) => {
        const id = ++requestId.current;
        setLoading(true);
        customerServices.searchByCompany({ q: q || undefined, limit: LIMIT })
            .then((data) => {
                if (id !== requestId.current) return;
                const list = Array.isArray(data) ? data : (data?.data || []);
                setOptions(list);
            })
            .catch(() => { if (id === requestId.current) setOptions([]); })
            .finally(() => { if (id === requestId.current) setLoading(false); });
    }, 300)).current;

    useEffect(() => {
        search(input);
    }, [input, search]);

    useEffect(() => () => search.cancel(), [search]);

    // El elegido siempre esta en la lista, aunque la busqueda actual no lo traiga: si no, el
    // Autocomplete lo da por invalido y avisa en consola.
    const allOptions = value && !_.some(options, { id: value.id }) ? [value, ...options] : options;

    return (
        <Autocomplete
            options={allOptions}
            loading={loading}
            value={value || null}
            disabled={disabled}
            filterOptions={(x) => x}
            getOptionLabel={(c) => c?.name || ''}
            isOptionEqualToValue={(a, b) => a?.id === b?.id}
            onInputChange={(e, text, reason) => { if (reason === 'input') setInput(text); }}
            onChange={(e, customer) => onChange(customer ? { id: customer.id, name: customer.name } : null)}
            noOptionsText={input ? 'Sin resultados' : 'Escribe para buscar'}
            renderOption={(props, c) => (
                <li {...props} key={c.id}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                        <StorefrontOutlinedIcon fontSize="small" color="action" />
                        <Stack>
                            <Typography variant="body2">{c.name}</Typography>
                            {c.code && <Typography variant="caption" color="text.secondary">{c.code}</Typography>}
                        </Stack>
                    </Stack>
                </li>
            )}
            renderInput={(params) => (
                <TextField
                    {...params}
                    label="Cliente *"
                    error={error}
                    helperText={helperText || 'El usuario verá solo los pedidos de este cliente'}
                    InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                            <>
                                {loading ? <CircularProgress color="inherit" size={18} /> : null}
                                {params.InputProps.endAdornment}
                            </>
                        )
                    }}
                />
            )}
        />
    );
};

export default ClientCustomerSelect;
